import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaChartPie } from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const MutualFundReturnsCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [investmentType, setInvestmentType] = useState('sip');
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [lumpsumAmount, setLumpsumAmount] = useState(100000);
  const [nav, setNav] = useState(100);
  const [currentNav, setCurrentNav] = useState(150);
  const [years, setYears] = useState(5);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [investmentType, monthlyInvestment, lumpsumAmount, nav, currentNav, years]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    if (investmentType === 'sip') {
      // SIP calculations
      const months = years * 12;
      let totalUnits = 0;
      let totalInvestment = 0;
      
      // Simplified: assuming NAV grows linearly (for illustration)
      const navGrowthPerMonth = (currentNav - nav) / months;
      
      const sipData = [];
      for (let m = 1; m <= months; m++) {
        const monthNav = nav + (navGrowthPerMonth * (m - 1));
        const units = monthlyInvestment / monthNav;
        totalUnits += units;
        totalInvestment += monthlyInvestment;
        
        if (m % 12 === 0 || m === months) {
          sipData.push({
            period: `Year ${Math.ceil(m / 12)}`,
            invested: totalInvestment,
            units: totalUnits.toFixed(3),
            value: Math.round(totalUnits * currentNav),
          });
        }
      }
      
      const currentValue = totalUnits * currentNav;
      const gain = currentValue - totalInvestment;
      const absoluteReturn = (gain / totalInvestment) * 100;
      const xirr = calculateXIRR(totalInvestment, currentValue, years);
      
      setResult({
        totalInvestment,
        totalUnits,
        currentValue: Math.round(currentValue),
        gain: Math.round(gain),
        absoluteReturn,
        cagr: xirr,
        sipData,
        averageNav: (totalInvestment / totalUnits).toFixed(2),
      });
    } else {
      // Lumpsum calculations
      const units = lumpsumAmount / nav;
      const currentValue = units * currentNav;
      const gain = currentValue - lumpsumAmount;
      const absoluteReturn = (gain / lumpsumAmount) * 100;
      const cagr = (Math.pow(currentValue / lumpsumAmount, 1 / years) - 1) * 100;
      
      setResult({
        totalInvestment: lumpsumAmount,
        totalUnits: units,
        currentValue: Math.round(currentValue),
        gain: Math.round(gain),
        absoluteReturn,
        cagr,
        averageNav: nav,
      });
    }
  };

  // Simplified XIRR approximation for SIP
  const calculateXIRR = (invested, finalValue, years) => {
    // Using simplified formula for SIP returns
    // More accurate would require iterative XIRR calculation
    const n = years * 12;
    const rate = (Math.pow(finalValue / invested, 2 / (n + 1)) - 1) * 12 * 100;
    return Math.min(rate, 100); // Cap at 100% for reasonable display
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Mutual Fund Returns Report', {
      'Investment Type': investmentType === 'sip' ? 'SIP' : 'Lumpsum',
      'Total Investment': formatCurrency(result.totalInvestment),
      'NAV at Purchase': formatCurrency(nav),
      'Current NAV': formatCurrency(currentNav),
      'Total Units': result.totalUnits.toFixed(3),
      'Current Value': formatCurrency(result.currentValue),
      'Gain/Loss': formatCurrency(result.gain),
      'Absolute Return': `${result.absoluteReturn.toFixed(2)}%`,
      'CAGR/XIRR': `${result.cagr.toFixed(2)}%`,
    });
  };

  const handleReset = () => {
    setInvestmentType('sip');
    setMonthlyInvestment(10000);
    setLumpsumAmount(100000);
    setNav(100);
    setCurrentNav(150);
    setYears(5);
  };

  const chartData = result ? {
    labels: ['Investment', 'Gain'],
    datasets: [{
      data: [result.totalInvestment, Math.max(0, result.gain)],
      backgroundColor: ['#3b82f6', '#10b981'],
      borderWidth: 0,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Mutual Fund Returns Calculator"
        description="Free Mutual Fund Returns Calculator - Calculate absolute, CAGR, XIRR returns on MF investments."
        keywords="mutual fund returns calculator, MF returns calculator, CAGR calculator mutual fund, XIRR calculator, mutual fund performance calculator"
        canonicalPath="/mf-returns"
      />
      <div className="calculator-header">
        <h1><FaChartPie style={{ color: '#ec4899' }} /> Mutual Fund Returns Calculator</h1>
        <p>Calculate your mutual fund investment returns (SIP or Lumpsum)</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Type</label>
            <div className="tabs">
              <button 
                className={`tab ${investmentType === 'sip' ? 'active' : ''}`}
                onClick={() => setInvestmentType('sip')}
              >
                SIP
              </button>
              <button 
                className={`tab ${investmentType === 'lumpsum' ? 'active' : ''}`}
                onClick={() => setInvestmentType('lumpsum')}
              >
                Lumpsum
              </button>
            </div>
          </div>

          {investmentType === 'sip' ? (
            <div className="input-group">
              <label>Monthly SIP Amount (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={monthlyInvestment === '' ? '' : monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
                  min="500"
                />
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="500"
                  max="100000"
                  step="500"
                  value={monthlyInvestment || 0}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                />
              </div>
            </div>
          ) : (
            <div className="input-group">
              <label>Lumpsum Amount (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={lumpsumAmount === '' ? '' : lumpsumAmount}
                  onChange={(e) => setLumpsumAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  min="1000"
                />
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="1000"
                  max="10000000"
                  step="1000"
                  value={lumpsumAmount || 0}
                  onChange={(e) => setLumpsumAmount(Number(e.target.value))}
                />
              </div>
            </div>
          )}

          <div className="input-group">
            <label>NAV at Purchase (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={nav === '' ? '' : nav}
                onChange={(e) => setNav(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Current NAV (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={currentNav === '' ? '' : currentNav}
                onChange={(e) => setCurrentNav(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Investment Period (Years)</label>
            <input
              type="number"
              value={years === '' ? '' : years}
              onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="30"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                value={years || 0}
                onChange={(e) => setYears(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ 
                background: result.gain >= 0 
                  ? 'linear-gradient(135deg, #ec4899, #db2777)'
                  : 'linear-gradient(135deg, #ef4444, #dc2626)'
              }}>
                <h3>Current Value</h3>
                <div className="amount">{formatCurrency(result.currentValue)}</div>
                <small>{result.gain >= 0 ? 'Gain' : 'Loss'}: {formatCurrency(Math.abs(result.gain))} ({result.absoluteReturn.toFixed(2)}%)</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Investment</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item" style={{ background: '#faf5ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">{investmentType === 'sip' ? 'XIRR' : 'CAGR'}</span>
                  <span className="value" style={{ color: '#8b5cf6' }}>{result.cagr.toFixed(2)}%</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Units</span>
                  <span className="value">{result.totalUnits.toFixed(3)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Average NAV</span>
                  <span className="value">₹{result.averageNav}</span>
                </div>
                <div className="result-item">
                  <span className="label">NAV Change</span>
                  <span className="value" style={{ color: currentNav >= nav ? '#10b981' : '#ef4444' }}>
                    {((currentNav - nav) / nav * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* Chart */}
              {shouldRenderChart && chartData && (
                <div style={{ maxWidth: '220px', margin: '20px auto' }}>
                  <Pie data={chartData} options={{ plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              {/* SIP Year-wise breakdown */}
              {investmentType === 'sip' && result.sipData && (
                <div className="breakup-section">
                  <h4>Year-wise Breakdown</h4>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Invested</th>
                        <th>Units</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sipData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.period}</td>
                          <td>{formatCurrency(row.invested)}</td>
                          <td>{row.units}</td>
                          <td style={{ color: '#10b981', fontWeight: 'bold' }}>{formatCurrency(row.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Mutual Fund Returns</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li><strong>Absolute Return:</strong> Total gain as % of investment</li>
              <li><strong>CAGR:</strong> Annualized return for lumpsum</li>
              <li><strong>XIRR:</strong> Annualized return for SIP (considers timing)</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>SIP provides rupee cost averaging benefit</li>
              <li>LTCG tax: 12.5% above ₹1.25 lakh (equity funds)</li>
              <li>STCG tax: 20% for holdings less than 1 year</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MutualFundReturnsCalculator;
